/** @jsxImportSource @emotion/react */
'use client'

import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type React from 'react'
import { useState } from 'react'
import { LoginApi } from '../../../services'
import { FlexCol } from '../../layout'
import { styles } from './styles'

export interface RecoverPasswordFormProps {
  /** Pre-fill from whatever the user already typed in the login form. */
  initialEmail: string
  onBack: () => void
}

export default function RecoverPasswordForm({
  initialEmail,
  onBack,
}: RecoverPasswordFormProps) {
  const [email, setEmail] = useState(initialEmail)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!email.trim()) {
      setError('Please enter your email address.')
      return
    }
    setError(null)
    setIsSubmitting(true)
    try {
      await new LoginApi().recoverPassword({ email })
      setSent(true)
    } catch (err: any) {
      setError(
        err?.body?.detail ||
          err?.response?.detail ||
          err?.message ||
          'Something went wrong. Please try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <FlexCol gap="lg">
        <FlexCol gap="xs">
          <Typography
            variant="h1"
            component="h1"
            align="center"
            css={styles.headingFont}
          >
            Password Recovery
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center">
            A password recovery email will be sent to the registered account.
          </Typography>
        </FlexCol>

        <FlexCol gap="md">
          {sent ? (
            <Typography role="status" color="success.main" variant="body2">
              Email sent — check your inbox for a link to reset your password.
            </Typography>
          ) : (
            <>
              <TextField
                id="recover_email"
                label="Email"
                type="email"
                autoComplete="email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              {error && (
                <Typography role="alert" color="error" variant="body2">
                  {error}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
              >
                Continue
              </Button>
            </>
          )}
          <Button type="button" variant="text" onClick={onBack}>
            Back to login
          </Button>
        </FlexCol>
      </FlexCol>
    </form>
  )
}
